package com.appsyde.shared.di

import com.appsyde.shared.network.createHttpClient
import com.appsyde.shared.modules.auth.data.AuthRepositoryImpl
import com.appsyde.shared.modules.auth.domain.repo.AuthRepository
import com.appsyde.shared.modules.auth.domain.usecase.CheckUserSessionUseCase
import com.appsyde.shared.modules.auth.domain.usecase.GetLoginUrlLandingUseCase
import com.appsyde.shared.modules.auth.domain.usecase.GetLoginUrlUseCase
import com.appsyde.shared.modules.auth.domain.usecase.GetSignupUrlUseCase
import com.appsyde.shared.app.presentation.inbox.ExtractOrchestrator
import com.appsyde.shared.app.presentation.account.AccountViewModel
import com.appsyde.shared.app.presentation.ticket.TicketViewModel
import com.appsyde.shared.app.presentation.tickets.TicketsListViewModel
import com.appsyde.shared.app.presentation.SessionViewModel
import com.appsyde.shared.modules.account.data.source.AccountRemoteDataSource
import com.appsyde.shared.modules.account.data.source.AccountRemoteDataSourceImpl
import com.appsyde.shared.modules.account.data.source.ConfirmDeletionDataSource
import com.appsyde.shared.modules.account.data.source.ConfirmDeletionDataSourceImpl
import com.appsyde.shared.modules.invite.data.source.InviteAcceptDataSource
import com.appsyde.shared.modules.invite.data.source.InviteAcceptDataSourceImpl
import com.appsyde.shared.modules.account.data.repository.AccountRepositoryImpl
import com.appsyde.shared.modules.account.domain.repository.AccountRepository
import com.appsyde.shared.modules.account.domain.usecase.CompleteOnboardingUseCase
import com.appsyde.shared.modules.account.domain.usecase.CreateCheckoutSessionUseCase
import com.appsyde.shared.modules.account.domain.usecase.CreateCustomerPortalSessionUseCase
import com.appsyde.shared.modules.account.domain.usecase.GetAccountUseCase
import com.appsyde.shared.modules.account.domain.usecase.GetBillingStatusUseCase
import com.appsyde.shared.modules.account.domain.usecase.GetMeUseCase
import com.appsyde.shared.modules.account.domain.usecase.RequestAccountDeletionUseCase
import com.appsyde.shared.modules.account.domain.usecase.UpdateAccountSignatureUseCase
import com.appsyde.shared.modules.account.domain.usecase.UpdateAccountToneUseCase
import com.appsyde.shared.modules.team.data.source.TeamRemoteDataSource
import com.appsyde.shared.modules.team.data.source.TeamRemoteDataSourceImpl
import com.appsyde.shared.app.presentation.team.TeamViewModel
import com.appsyde.shared.backendBaseUrl
import com.appsyde.shared.modules.ai.data.repository.AiRepositoryV2Impl
import com.appsyde.shared.modules.ai.data.source.AiRemoteDataSource
import com.appsyde.shared.modules.ai.data.source.AiRemoteDataSourceImpl
import com.appsyde.shared.modules.ai.domain.repository.AiRepositoryV2
import com.appsyde.shared.modules.ai.domain.usecase.GenerateAiExtractUseCase
import com.appsyde.shared.modules.ai.domain.usecase.PreloadAiExtractsUseCase
import com.appsyde.shared.modules.ai.domain.usecase.RegenerateContextUseCase
import com.appsyde.shared.modules.ai.domain.usecase.RefineTextUseCaseV2
import com.appsyde.shared.modules.ticket.data.repository.EmailRepositoryImpl
import com.appsyde.shared.modules.ticket.data.repository.TicketsRepositoryImpl
import com.appsyde.shared.modules.ticket.data.source.EmailRemoteDataSource
import com.appsyde.shared.modules.ticket.data.source.EmailRemoteDataSourceImpl
import com.appsyde.shared.modules.ticket.data.source.TicketsRemoteDataSource
import com.appsyde.shared.modules.ticket.data.source.TicketsRemoteDataSourceImpl
import com.appsyde.shared.modules.ticket.domain.repository.EmailRepository
import com.appsyde.shared.modules.ticket.domain.repository.TicketsRepository
import com.appsyde.shared.modules.auth.domain.usecase.LogoutUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.AcquireLockUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.AssignTicketUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.AssignUpToUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.GetTicketHistoryUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.GetTicketOriginalHistoryUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.AddMemoryUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.ConfirmKeyUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.GetTicketRespondStateUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.ListTicketsUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.RefreshTicketThreadUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.ReleaseLockUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.RenewLockUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.SendReplyUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.TakeoverTicketUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.UnassignTicketUseCase
import io.ktor.client.HttpClient
import org.koin.core.Koin
import org.koin.core.context.startKoin
import org.koin.core.module.Module
import org.koin.dsl.module

val sharedModule: Module = module {
    factory<EmailRemoteDataSource> { EmailRemoteDataSourceImpl(get(), backendBaseUrl()) }
    factory<EmailRepository> { EmailRepositoryImpl(get()) }
    factory<AuthRepository> { AuthRepositoryImpl(get(), backendBaseUrl()) }
    factory<HttpClient> { createHttpClient() }
    factory { GetLoginUrlUseCase(get()) }
    factory { GetSignupUrlUseCase(get()) }
    factory { GetLoginUrlLandingUseCase(get()) }
    factory { RefineTextUseCaseV2(get()) }
    factory { CheckUserSessionUseCase(get()) }
    factory { LogoutUseCase(get()) }
    single { SessionViewModel(get(), get(), get(), get(), get()) }

    factory<TicketsRemoteDataSource> { TicketsRemoteDataSourceImpl(get(), backendBaseUrl()) }
    factory<TicketsRepository> { TicketsRepositoryImpl(get<TicketsRemoteDataSource>()) }
    factory<AiRemoteDataSource> { AiRemoteDataSourceImpl(get(), backendBaseUrl()) }
    factory<AiRepositoryV2> { AiRepositoryV2Impl(get<AiRemoteDataSource>()) }
    factory { GenerateAiExtractUseCase(get()) }
    factory { PreloadAiExtractsUseCase(get()) }
    single { ExtractOrchestrator(get(), get()) }
    factory { RegenerateContextUseCase(get()) }
    factory { GetTicketRespondStateUseCase(get()) }
    factory { RefreshTicketThreadUseCase(get()) }
    factory { ListTicketsUseCase(get()) }
    factory { SendReplyUseCase(get()) }
    factory { com.appsyde.shared.modules.ticket.domain.usecase.UpdateTicketStatusUseCase(get()) }
    factory { AddMemoryUseCase(get()) }
    factory { ConfirmKeyUseCase(get()) }
    factory { GetTicketHistoryUseCase(get()) }
    factory { GetTicketOriginalHistoryUseCase(get()) }
    factory { AssignTicketUseCase(get()) }
    factory { AssignUpToUseCase(get()) }
    factory { TakeoverTicketUseCase(get()) }
    factory { UnassignTicketUseCase(get()) }
    factory { AcquireLockUseCase(get()) }
    factory { RenewLockUseCase(get()) }
    factory { ReleaseLockUseCase(get()) }
    factory { TicketViewModel(get(), get(), get(), get(), get(), get(), get(), get(), get(), get(), get(), get(), get(), get(), get(), get(), get()) }
    factory { TicketsListViewModel(get(), get(), get(), get()) }

    factory<AccountRemoteDataSource> { AccountRemoteDataSourceImpl(get(), backendBaseUrl()) }
    factory<ConfirmDeletionDataSource> { ConfirmDeletionDataSourceImpl(get(), backendBaseUrl()) }
    factory<InviteAcceptDataSource> { InviteAcceptDataSourceImpl(get(), backendBaseUrl()) }
    factory<AccountRepository> { AccountRepositoryImpl(get()) }
    factory { GetAccountUseCase(get()) }
    factory { GetMeUseCase(get()) }
    factory { CompleteOnboardingUseCase(get()) }
    factory { UpdateAccountToneUseCase(get()) }
    factory { UpdateAccountSignatureUseCase(get()) }
    factory { RequestAccountDeletionUseCase(get()) }
    factory { GetBillingStatusUseCase(get()) }
    factory { CreateCheckoutSessionUseCase(get()) }
    factory { CreateCustomerPortalSessionUseCase(get()) }
    factory<TeamRemoteDataSource> { TeamRemoteDataSourceImpl(get(), backendBaseUrl()) }
    factory<com.appsyde.shared.modules.team.domain.repository.TeamRepository> {
        com.appsyde.shared.modules.team.data.repository.TeamRepositoryImpl(get())
    }
    factory { com.appsyde.shared.modules.team.domain.usecase.GetTeamUsersUseCase(get()) }
    factory { com.appsyde.shared.modules.team.domain.usecase.GetTeamInvitesUseCase(get()) }
    factory { com.appsyde.shared.modules.team.domain.usecase.CreateInviteUseCase(get()) }
    factory { com.appsyde.shared.modules.team.domain.usecase.RevokeInviteUseCase(get()) }
    factory { com.appsyde.shared.modules.team.domain.usecase.DisableUserUseCase(get()) }
    factory {
        TeamViewModel(
            get(),
            get(),
            get(),
            get(),
            get()
        )
    }
    single { AccountViewModel(get(), get(), get(), get(), get(), get(), get(), get()) }
}

internal var koinInstance: Koin? = null

fun initKoin() {
    val koinApplication = startKoin {
        modules(sharedModule)
    }
    koinInstance = koinApplication.koin
}
